import React, { useState } from 'react';
import { IoMdCheckmark, IoMdTime } from 'react-icons/io';
import { FaTimes, FaRobot } from 'react-icons/fa';
import { HiLightningBolt } from 'react-icons/hi';

import { useBotSessions } from '../../context/BotSessionsProvider';
import SessionDetailsModal from './SessionDetailsModal';

const WorkflowExecutions = () => {
  const [hoveredIndex, setHoveredIndex] = useState(null);
  // const [botSessions, setBotSessions] = useState([]);
  const { botSessions } = useBotSessions(); // Get sessions from context.
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedSession, setSelectedSession] = useState(null);

  // Basic size settings for stacking cards (Updated for new UI)
  const fullHeight = 124; // Compact professional height
  const collapsedPeek = 28; // visible part when not hovered

  const computeTranslateY = (index) => {
    if (hoveredIndex === null) {
      return index * collapsedPeek;
    } else {
      if (index <= hoveredIndex) {
        return index * collapsedPeek;
      } else {
        const distance = index - hoveredIndex;
        let extraOffset = fullHeight;

        // Add small gaps for the cards immediately following the active one
        if (distance === 1) extraOffset = fullHeight + 6;
        else if (distance >= 2) extraOffset += 12;

        return (
          hoveredIndex * collapsedPeek +
          extraOffset +
          (distance - 1) * collapsedPeek
        );
      }
    }
  };

  const computeStyle = (index) => {
    const translateY = computeTranslateY(index);
    const distanceFromHovered =
      hoveredIndex === null ? 0 : Math.min(Math.abs(index - hoveredIndex), 3);

    // Improved Scaling Logic for smoother look (replaces old logic)
    let scale = 1;
    let opacity = 1;

    if (hoveredIndex !== null) {
      if (distanceFromHovered === 0) {
        scale = 1.02; // Highlight active card
        opacity = 1;
      } else if (index < hoveredIndex) {
        // Cards above the hovered one
        scale = 1 - distanceFromHovered * 0.02;
        opacity = 0.95;
      } else {
        // Cards below the hovered one
        scale = 0.98;
        opacity = 0.9;
      }
    } else {
      // Resting state: slight stacking scale effect
      scale = 1 - index * 0.01;
    }

    // Switched from RotateX to Scale/Translate for a cleaner "Professional" look
    // The previous rotateX caused jagged edges on text.
    let transform = `translate3d(0, ${translateY}px, 0) scale(${scale})`;

    const zIndex =
      hoveredIndex === null
        ? botSessions.length - index
        : index === hoveredIndex
          ? 100
          : 50 - Math.abs(index - hoveredIndex); // Ensure active card is always on top

    return {
      transform,
      opacity,
      zIndex,
      height: `${fullHeight}px`,
      // transformStyle: "preserve-3d", // Removed as we aren't using rotation anymore to fix the look
      transition: 'all 0.4s cubic-bezier(0.25, 0.8, 0.25, 1)', // Smooth spring transition
    };
  };

  const containerHeight =
    hoveredIndex === null
      ? botSessions.length * collapsedPeek + fullHeight
      : (() => {
          const lastIndex = botSessions.length - 1;
          return computeTranslateY(lastIndex) + fullHeight;
        })();

  // Open modal on session card click
  const handleSessionClick = (session) => {
    setSelectedSession(session);
    setIsModalOpen(true);
  };

  return (
    <div className="pl-5 pr-5">
      {/* Parent container */}
      <div
        className="relative mx-auto w-full max-w-md"
        style={{ height: containerHeight }}
      >
        {botSessions.map((session, index) => (
          // eslint-disable-next-line
          <div
            key={session.session_id}
            style={computeStyle(index)}
            className="absolute left-0 right-0 cursor-pointer will-change-transform"
            // Only update hoveredIndex on mouse enter.
            onMouseEnter={() => setHoveredIndex(index)}
            onClick={() => handleSessionClick(session)}
            // Remove onMouseLeave to keep the last hovered card active (per your original code)
            // or add it back if you want it to collapse when mouse leaves the specific card
            onMouseLeave={() => setHoveredIndex(null)}
          >
            <SessionCard session={session} isHovered={hoveredIndex === index} />
          </div>
        ))}
      </div>
      {/* Modal for session details */}
      {selectedSession && (
        <SessionDetailsModal
          session={selectedSession}
          isOpen={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
            setSelectedSession(null);
          }}
        />
      )}
    </div>
  );
};

// --- NEW PROFESSIONAL CARD COMPONENT ---
const SessionCard = ({ session, isHovered }) => {
  const { status, session_id, botTitle } = session;
  const lowerStatus = status?.toLowerCase() || 'running';

  // Professional Theme Configuration
  const themes = {
    running: {
      bg: 'bg-amber-500/10',
      border: 'border-amber-500/20',
      text: 'text-amber-500',
      icon: <HiLightningBolt className="text-[10px]" />,
      bar: 'bg-amber-500',
    },
    success: {
      bg: 'bg-emerald-500/10',
      border: 'border-emerald-500/20',
      text: 'text-emerald-500',
      icon: <IoMdCheckmark className="text-[10px]" />,
      bar: 'bg-emerald-500',
    },
    failed: {
      bg: 'bg-rose-500/10',
      border: 'border-rose-500/20',
      text: 'text-rose-500',
      icon: <FaTimes className="text-[10px]" />,
      bar: 'bg-rose-500',
    },
  };

  const theme = themes[lowerStatus] || themes.running;

  return (
    <div
      className={`
        relative flex h-full w-full flex-col overflow-hidden rounded-lg border
        bg-zinc-900/95 backdrop-blur-md transition-all duration-300 ease-out
        ${
          isHovered
            ? `translate-y-[-2px] border-zinc-700 shadow-xl`
            : 'border-zinc-800 shadow-sm'
        }
      `}
    >
      {/* Content Container */}
      <div className="flex flex-1 flex-col justify-between p-3.5">
        {/* Top Row: Icon, Title, Badge */}
        <div className="flex items-start justify-between gap-3">
          <div className="flex min-w-0 flex-1 items-center gap-3">
            {/* Minimal Icon Box */}
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-zinc-700/50 bg-zinc-800">
              <FaRobot className="text-sm text-zinc-400" />
            </div>

            {/* Title & Truncated ID */}
            <div className="flex min-w-0 flex-1 flex-col">
              <span className="truncate text-sm font-semibold leading-tight text-zinc-100">
                {botTitle || 'Untitled Bot'}
              </span>
              <span className="mt-1 w-full truncate font-mono text-[10px] text-zinc-500 opacity-70">
                ID: {session_id}
              </span>
            </div>
          </div>

          {/* Small Status Pill */}
          <div
            className={`
            flex shrink-0 items-center gap-1.5 rounded-full border px-2 py-1
            ${theme.bg} ${theme.border}
          `}
          >
            {lowerStatus === 'running' && (
              <div className="h-1.5 w-1.5 animate-pulse rounded-full bg-amber-500" />
            )}
            {lowerStatus !== 'running' && theme.icon}
            <span
              className={`text-[9px] font-semibold uppercase tracking-wide ${theme.text}`}
            >
              {status}
            </span>
          </div>
        </div>

        {/* Bottom Row: Footer Info & Visual Indicator */}
        <div className="mt-2 flex items-center justify-between border-t border-zinc-800/50 pt-2">
          <div className="flex items-center gap-1.5">
            <IoMdTime className="text-xs text-zinc-500" />
            <span className="text-[10px] font-medium text-zinc-500">
              Just now
            </span>
          </div>

          {/* Subtle Status Line/Progress Bar */}
          <div className="h-0.5 w-14 overflow-hidden rounded-full bg-zinc-800">
            <div
              className={`h-full w-full ${lowerStatus === 'running' ? 'origin-left animate-pulse' : ''} ${theme.bar}`}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default WorkflowExecutions;
